<script lang="ts">
	import {
		Text,
		Container,
		Toggle,
		Input,
		PreviewBox,
		PreviewImageContent,
		Collapse,
		Button,
		ButtonGroupV2,
	} from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { devices } from 'src/stores/lists/devices';
	import { isCameraDevice } from 'src/helpers/interface_helpers';
	import { isTestUser } from 'src/helpers/user_helpers';
	import { getDashWidgetPreviewSrc, resolveCameraLayout } from '../../dash-camera-helpers';
	import { normalizeDeviceCardConfig } from '../../dash-device-card-config';
	import type {
		DashCameraWidgetFeedMode,
		DashDeviceControlPlacement,
		DashWidgetEditType,
	} from '../../dash-types';
	import { isDashEnergyWidgetType, type DashEnergyWidgetTypes } from '../../dash-energy-types';
	import { genEnergyWidgetData, selectItemsToInfoCategories } from '../../dash-energy-helpers';
	import DashWidgetCard from '../../DashWidgets/DashWidgetCard.svelte';
	import DashCameraWizardPreview from '../Components/DashCameraWizardPreview.svelte';

	export let editItem: DashWidgetEditType;

	const showDevFeedSettings = isTestUser();
	let advancedExpanded = false;

	$: isCameraWidget = editItem.widgetType === 'dashCamera';
	$: isEnergyWidget = isDashEnergyWidgetType(editItem.widgetType);
	$: isControlCardWidget = editItem.widgetType === 'dashQuickDev';
	$: energyPreviewConfig =
		editItem.widgetType === 'dashEnergyInfo'
			? {
					...editItem.energyConfig,
					infoCategories: selectItemsToInfoCategories(editItem.selectItems),
				}
			: editItem.energyConfig;
	$: energyPreviewWidget = isEnergyWidget
		? genEnergyWidgetData({
				type: editItem.widgetType as DashEnergyWidgetTypes,
				title: editItem.widgetTitle,
				energyConfig: energyPreviewConfig,
			})
		: null;
	$: if (isEnergyWidget && editItem.showWidgetTitle !== false) editItem.showWidgetTitle = false;
	$: cameraVariant = editItem.cameraConfig?.variant || 'auto';

	$: selectedCameras = (editItem.selectItems || [])
		.map((item) => {
			const device = $devices[item.id];
			if (!device || !isCameraDevice(device)) return null;
			return { id: String(item.id), name: device.name || String(item.id) };
		})
		.filter(Boolean) as { id: string; name: string }[];

	$: resolvedLayout = resolveCameraLayout(editItem.cameraConfig, selectedCameras.length);
	$: isTrioVariant = resolvedLayout === 'triple-focus';
	$: wizardPreviewLayout = resolvedLayout === 'button-card' ? 'single' : resolvedLayout;

	$: showFeedModeSettings =
		showDevFeedSettings &&
		selectedCameras.length > 0 &&
		(editItem.widgetType === 'dashCardDev' ||
			editItem.widgetType === 'dashQuickDev' ||
			(isCameraWidget && !isTrioVariant));

	$: showTrioFeedSettings = showDevFeedSettings && isCameraWidget && isTrioVariant;
	$: selectedFeedMode = editItem.cameraConfig?.feedMode || 'live';
	$: selectedSideFeedMode = editItem.cameraConfig?.sideFeedMode || 'live';
	$: previewImgSrc = getDashWidgetPreviewSrc(editItem.widgetType, cameraVariant);

	$: selectedControlPlacement = normalizeDeviceCardConfig(editItem.deviceCardConfig).controlPlacement;
	$: controlPlacementTagline =
		selectedControlPlacement === 'button'
			? $format('label_dash_widget_control_tagline_button', {
					default: 'Use the button to toggle. Tap the card for settings.',
				})
			: $format('label_dash_widget_control_tagline_card', {
					default: 'Tap anywhere on the card to toggle the device.',
				});

	function patchCameraConfig(patch: Record<string, unknown>) {
		editItem.cameraConfig = {
			...editItem.cameraConfig,
			...(isCameraWidget ? { variant: cameraVariant } : {}),
			feedMode: editItem.cameraConfig?.feedMode ?? 'live',
			mainAlwaysLive: true,
			...patch,
		};
	}

	function setFeedMode(feedMode: DashCameraWidgetFeedMode) {
		patchCameraConfig({ feedMode, cameraFeedModes: undefined });
	}

	function setSideFeedMode(sideFeedMode: DashCameraWidgetFeedMode) {
		patchCameraConfig({ sideFeedMode, mainAlwaysLive: true });
	}

	function setControlPlacement(placement: DashDeviceControlPlacement) {
		editItem.deviceCardConfig = placement === 'button' ? { controlPlacement: 'button' } : undefined;
	}
</script>

{#if !isEnergyWidget}
	<Text class="mb-s text-uppercase text-color-muted pl-s-plus" styleType="label-large">
		{$format('label_dash_widget_group_settings', {
			default: 'Widget group settings',
		})}
	</Text>

	<Container type="borderSolid" class="mb-l" padding="12">
		<Toggle id="1" bind:checked={editItem.showWidgetTitle} size="m">
			{$format('label_dash_widget_show_title', { default: 'Show title' })}
		</Toggle>

		{#if editItem.showWidgetTitle}
			<Text class="mb-s mt-s-plus" styleType="body-small">
				{$format('label_dash_widget_group_title', { default: 'Widget group title' })}
			</Text>
			<Input
				icon={null}
				bind:value={editItem.widgetTitle}
				id="widgetGroupTitle"
				aria-label={$format('label_dash_widget_input_widget_title', {
					default: 'Input widget title',
				})}
				label={$format('label_dash_widget_input_widget_title', {
					default: 'Input widget title',
				})}
				aria-labelledby="input-widget-title"
			/>
		{/if}
	</Container>
{/if}

{#if isControlCardWidget}
	<Collapse
		id="dash-widget-control-advanced"
		class="mb-l"
		title={$format('label_dash_widget_advanced', { default: 'Advanced' })}
		expanded={advancedExpanded}
		on:click={() => (advancedExpanded = !advancedExpanded)}
	>
		<Text styleType="label-large" class="mb-xs text-uppercase text-color-muted">
			{$format('label_dash_widget_control_placement', { default: 'Device control' })}
		</Text>
		<ButtonGroupV2
			id="dash-widget-control-placement"
			class="mb-s"
			aria-label={$format('label_dash_widget_control_placement', {
				default: 'Device control',
			})}
			noWrap
			orientation="horizontal"
		>
			<Button
				class={selectedControlPlacement === 'card' ? 'active' : ''}
				size="m"
				aria-pressed={selectedControlPlacement === 'card'}
				on:click={() => setControlPlacement('card')}
			>
				{$format('label_dash_widget_control_chip_card', { default: 'Tap card' })}
			</Button>
			<Button
				class={selectedControlPlacement === 'button' ? 'active' : ''}
				size="m"
				aria-pressed={selectedControlPlacement === 'button'}
				on:click={() => setControlPlacement('button')}
			>
				{$format('label_dash_widget_control_chip_button', { default: 'Button' })}
			</Button>
		</ButtonGroupV2>
		<Text styleType="body-small" class="text-color-muted">
			{controlPlacementTagline}
		</Text>
	</Collapse>
{/if}

{#if showFeedModeSettings}
	<Text class="mb-s text-uppercase text-color-muted pl-s-plus" styleType="label-large">
		{$format('label_camera_widget_feed_mode', {
			default: 'Camera feed',
		})}
	</Text>

	<Container type="borderSolid" class="mb-l" padding="12">
		<div class="camera-feed-mode-options">
			<label class="camera-feed-mode-card" class:selected={selectedFeedMode === 'preview'}>
				<input
					type="radio"
					name="camera-widget-feed-mode"
					value="preview"
					checked={selectedFeedMode === 'preview'}
					on:change={() => setFeedMode('preview')}
				/>
				<span>
					<Text styleType="body-small" class="text-600">
						{$format('label_camera_widget_preview_mode', {
							default: 'Snapshot',
						})}
					</Text>
					<Text styleType="body-extra-small" class="text-color-muted mt-xxs">
						{$format('label_camera_widget_snapshot_info', {
							default:
								'Shows the last camera thumbnail. Uses less bandwidth than a live stream.',
						})}
					</Text>
				</span>
			</label>

			<label class="camera-feed-mode-card" class:selected={selectedFeedMode === 'live'}>
				<input
					type="radio"
					name="camera-widget-feed-mode"
					value="live"
					checked={selectedFeedMode === 'live'}
					on:change={() => setFeedMode('live')}
				/>
				<span>
					<Text styleType="body-small" class="text-600">
						{$format('label_camera_widget_live_mode', {
							default: 'Live feed',
						})}
					</Text>
					<Text styleType="body-extra-small" class="text-color-muted mt-xxs">
						{$format('label_camera_widget_live_dot_info', {
							default: 'Live feed shows a red dot on the top-left of the device card.',
						})}
					</Text>
				</span>
			</label>
		</div>
	</Container>
{/if}

{#if showTrioFeedSettings}
	<Text class="mb-s text-uppercase text-color-muted pl-s-plus" styleType="label-large">
		{$format('label_camera_widget_side_feed_mode', {
			default: 'Side cameras',
		})}
	</Text>

	<Container type="borderSolid" class="mb-l" padding="12">
		<div class="camera-feed-mode-options">
			<label class="camera-feed-mode-card" class:selected={selectedSideFeedMode === 'preview'}>
				<input
					type="radio"
					name="camera-widget-side-feed-mode"
					value="preview"
					checked={selectedSideFeedMode === 'preview'}
					on:change={() => setSideFeedMode('preview')}
				/>
				<span>
					<Text styleType="body-small" class="text-600">
						{$format('label_camera_widget_preview_mode', {
							default: 'Snapshot',
						})}
					</Text>
					<Text styleType="body-extra-small" class="text-color-muted mt-xxs">
						{$format('label_camera_widget_snapshot_info', {
							default:
								'Shows the last camera thumbnail. Uses less bandwidth than a live stream.',
						})}
					</Text>
				</span>
			</label>

			<label class="camera-feed-mode-card" class:selected={selectedSideFeedMode === 'live'}>
				<input
					type="radio"
					name="camera-widget-side-feed-mode"
					value="live"
					checked={selectedSideFeedMode === 'live'}
					on:change={() => setSideFeedMode('live')}
				/>
				<span>
					<Text styleType="body-small" class="text-600">
						{$format('label_camera_widget_live_mode', {
							default: 'Live feed',
						})}
					</Text>
					<Text styleType="body-extra-small" class="text-color-muted mt-xxs">
						{$format('label_camera_widget_live_dot_info', {
							default: 'Live feed shows a red dot on the top-left of the device card.',
						})}
					</Text>
				</span>
			</label>
		</div>
	</Container>
{/if}

{#if isEnergyWidget && energyPreviewWidget}
	<div class="energy-widget-preview">
		<Text styleType="label-large" class="text-uppercase text-color-muted">
			{$format('label_dash_widget_group_preview', { default: 'Preview' })}
		</Text>
		<div class="energy-widget-preview-surface">
			<DashWidgetCard
				cards={energyPreviewWidget.cards}
				type={energyPreviewWidget.type}
				title={energyPreviewWidget.title}
				widgetId={`preview-${energyPreviewWidget.type}`}
				energyConfig={energyPreviewWidget.energyConfig}
				staticPreview
			/>
		</div>
	</div>
{:else}
	<PreviewBox
		fixedHeight={isCameraWidget ? 0 : 156}
		minHeight={isCameraWidget
			? resolvedLayout === 'quad'
				? 240
				: resolvedLayout === 'triple-focus'
					? 180
					: 140
			: 0}
		padding="m"
		title={$format('label_dash_widget_group_preview', {
			default: 'Preview',
		})}
		backgroundImageSrc="./images/loginBackground.png"
	>
		{#if isCameraWidget}
			<DashCameraWizardPreview cameras={selectedCameras} layout={wizardPreviewLayout} />
		{:else}
			<PreviewImageContent
				title={editItem.showWidgetTitle ? editItem.widgetTitle : ''}
				imgAlt={$format('label_dash_widget_preview_alt', {
					default: 'Dashboard widget preview',
				})}
				imgSrc={previewImgSrc}
			/>
		{/if}
	</PreviewBox>
{/if}

<style>
	.energy-widget-preview {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
	}

	.energy-widget-preview-surface {
		position: relative;
		min-width: 0;
		max-height: min(560px, 65vh);
		overflow: auto;
		padding: var(--spacing-m);
		border-radius: 16px;
		background: var(--color-0) url('/images/loginBackground.png') center / cover;
		pointer-events: none;
	}

	.energy-widget-preview-surface :global(.dash-energy-widget-frame) {
		min-height: 220px;
	}

	.camera-feed-mode-options {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: var(--spacing-s);
	}

	.camera-feed-mode-card {
		display: grid;
		grid-template-columns: auto minmax(0, 1fr);
		gap: var(--spacing-s);
		align-items: flex-start;
		padding: var(--spacing-s-plus);
		border: 1px solid var(--color-border);
		border-radius: var(--radius-big);
		background: var(--color-0);
		cursor: pointer;
	}

	.camera-feed-mode-card.selected {
		border-color: var(--color-primary);
		background: var(--color-primary-10);
	}

	.camera-feed-mode-card input {
		margin-top: 2px;
		accent-color: var(--color-primary);
	}

	@media (max-width: 639px) {
		.camera-feed-mode-options {
			grid-template-columns: 1fr;
		}
	}
</style>
